// Console diagnostics for tile load / merge timing.
// Disable: window.__tileDiag = false
// Enable (default): window.__tileDiag = true

function enabled() {
  return typeof window === 'undefined' || window.__tileDiag !== false;
}

function now() {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function pct(sorted, p) {
  if (!sorted.length) return 0;
  const i = Math.min(sorted.length - 1, Math.max(0, Math.floor((p / 100) * sorted.length)));
  return sorted[i];
}

function summarizeMs(samples) {
  if (!samples.length) return null;
  const s = samples.slice().sort((a, b) => a - b);
  const sum = s.reduce((a, b) => a + b, 0);
  return {
    n: s.length,
    min: +s[0].toFixed(1),
    p50: +pct(s, 50).toFixed(1),
    p95: +pct(s, 95).toFixed(1),
    max: +s[s.length - 1].toFixed(1),
    mean: +(sum / s.length).toFixed(1)
  };
}

class TileLoadDiagnostics {
  constructor() {
    this.waveId = 0;
    this.resetWave();
  }

  resetWave() {
    this.wave = {
      id: 0,
      t0: 0,
      visible: 0,
      pending: 0,
      zoom: null,
      completed: 0,
      failed: 0,
      geomCacheHits: 0,
      geomNetwork: 0,
      geomBytesNetwork: 0,
      geomBytesCache: 0,
      geomFetchMs: [],
      geomParseMs: [],
      fieldMs: [],
      tileTotalMs: [],
      renderMergeMs: [],
      renderReady: [],
      lastLogCompleted: 0,
      loggedComplete: false
    };
  }

  beginWave({visible, pending, zoom, lon, lat}) {
    if (!enabled()) return;
    this.waveId += 1;
    this.resetWave();
    this.wave.id = this.waveId;
    this.wave.t0 = now();
    this.wave.visible = visible;
    this.wave.pending = pending;
    this.wave.zoom = zoom;
    console.log(
      `[tile-diag] wave #${this.wave.id} start | visible=${visible} pendingLoad=${pending} ` +
        `zoom=${zoom?.toFixed?.(2) ?? zoom} lon=${lon?.toFixed?.(2)} lat=${lat?.toFixed?.(2)}`
    );
  }

  geometry(info) {
    if (!enabled()) return;
    const w = this.wave;
    if (info.source === 'cache') {
      w.geomCacheHits += 1;
      w.geomBytesCache += info.bytes || 0;
    } else {
      w.geomNetwork += 1;
      w.geomBytesNetwork += info.bytes || 0;
    }
    if (Number.isFinite(info.fetchMs)) w.geomFetchMs.push(info.fetchMs);
    if (Number.isFinite(info.parseMs)) w.geomParseMs.push(info.parseMs);

    if (window.__tileDiagVerbose) {
      console.log(
        `[tile-diag] geom ${info.z}/${info.x}/${info.y} ${info.source} ` +
          `fetch=${info.fetchMs?.toFixed?.(1)}ms parse=${info.parseMs?.toFixed?.(1)}ms ` +
          `bytes=${info.bytes} verts=${info.nVerts} tris=${info.nTris}`
      );
    }
  }

  field(info) {
    if (!enabled()) return;
    if (Number.isFinite(info.ms)) this.wave.fieldMs.push(info.ms);
    if (window.__tileDiagVerbose) {
      console.log(
        `[tile-diag] field ${info.z}/${info.x}/${info.y} ${info.kind} ` +
          `${info.ms?.toFixed?.(1)}ms bytes=${info.bytes ?? '-'} ${info.cached ? 'cached' : 'network'}`
      );
    }
  }

  tileDone(info) {
    if (!enabled()) return;
    const w = this.wave;
    if (info.ok) {
      w.completed += 1;
      if (Number.isFinite(info.totalMs)) w.tileTotalMs.push(info.totalMs);
    } else {
      w.failed += 1;
    }

    // Progress every 25 tiles, and always on the last pending.
    const every = 25;
    const shouldLog =
      w.completed + w.failed >= w.pending ||
      w.completed - w.lastLogCompleted >= every;
    if (shouldLog) {
      w.lastLogCompleted = w.completed;
      this.logProgress(info.queueLeft, info.inFlight);
    }
  }

  logProgress(queueLeft, inFlight) {
    if (!enabled()) return;
    const w = this.wave;
    const elapsed = now() - w.t0;
    const done = w.completed + w.failed;
    const rate = elapsed > 0 ? (w.completed / (elapsed / 1000)).toFixed(1) : '0';
    console.log(
      `[tile-diag] wave #${w.id} progress ${done}/${w.pending} ` +
        `(ok=${w.completed} fail=${w.failed}) elapsed=${(elapsed / 1000).toFixed(1)}s ` +
        `rate=${rate} tiles/s | geom cache/net=${w.geomCacheHits}/${w.geomNetwork} | ` +
        `queue=${queueLeft ?? '?'} inFlight=${inFlight ?? '?'}`
    );
  }

  render(info) {
    if (!enabled()) return;
    if (Number.isFinite(info.mergeMs)) this.wave.renderMergeMs.push(info.mergeMs);
    if (Number.isFinite(info.ready)) this.wave.renderReady.push(info.ready);
    if (window.__tileDiagVerbose || info.forceLog) {
      console.log(
        `[tile-diag] render ready=${info.ready}/${info.visible} ` +
          `merge=${info.mergeMs?.toFixed?.(1)}ms setProps=${info.setPropsMs?.toFixed?.(1)}ms ` +
          `verts=${info.vertCount ?? '-'}`
      );
    }
  }

  waveComplete({queueLeft = 0, inFlight = 0} = {}) {
    if (!enabled()) return;
    const w = this.wave;
    if (!w.id || w.loggedComplete) return;
    if (queueLeft + inFlight > 0) return;
    w.loggedComplete = true;

    const elapsed = now() - w.t0;
    const netMB = (w.geomBytesNetwork / (1024 * 1024)).toFixed(2);
    const cacheMB = (w.geomBytesCache / (1024 * 1024)).toFixed(2);
    console.log(
      `[tile-diag] wave #${w.id} COMPLETE in ${(elapsed / 1000).toFixed(2)}s | ` +
        `tiles ok/fail=${w.completed}/${w.failed} of ${w.pending} queued (visible=${w.visible})\n` +
        `  geometry: cacheHits=${w.geomCacheHits} network=${w.geomNetwork} ` +
        `downloaded=${netMB}MB fromCache=${cacheMB}MB\n` +
        `  geomFetchMs: ${JSON.stringify(summarizeMs(w.geomFetchMs))}\n` +
        `  geomParseMs: ${JSON.stringify(summarizeMs(w.geomParseMs))}\n` +
        `  fieldFetchMs: ${JSON.stringify(summarizeMs(w.fieldMs))}\n` +
        `  tileTotalMs:  ${JSON.stringify(summarizeMs(w.tileTotalMs))}\n` +
        `  mergeMs:      ${JSON.stringify(summarizeMs(w.renderMergeMs))}\n` +
        `  hint: high geomFetchMs/fieldFetchMs → network; high geomParseMs/mergeMs → CPU`
    );
  }
}

export const tileDiag = new TileLoadDiagnostics();
export {now as diagNow};
